import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, FileText, ShieldCheck, Building2 } from "lucide-react";

const CONTENT = {
  "how-it-works": {
    title: "How it works",
    eyebrow: "Guide",
    intro: "Real&State connects property owners with listed companies in three simple steps.",
    sections: [
      { icon: FileText, h: "1. Post a job", p: "Describe what you need — snagging, maintenance, renovation and more. Your exact address and contact details stay private." },
      { icon: Building2, h: "2. Compare responses", p: "Matching companies whose trade licence has been checked respond with quotes. Compare scope, availability, profile and price — not just the lowest number." },
      { icon: ClipboardCheck, h: "3. Choose & get it done", p: "Message companies through the platform, award the job, and leave a review once the work is completed." },
    ],
  },
  "for-businesses": {
    title: "List your company on Real&State",
    eyebrow: "For businesses",
    intro: "Reach property owners across Dubai. Upload your trade licence, get it checked, and respond to relevant jobs.",
    sections: [
      { icon: ShieldCheck, h: "Trade licence checked", p: "We confirm your licence document is readable and matches your stated identity and activities before your listing goes live. This builds trust with customers." },
      { icon: FileText, h: "Free launch pilot", p: "During the launch pilot, listed companies can respond to jobs within fair limits at no cost. Paid response credits and promotions can be enabled later by the platform." },
      { icon: Building2, h: "Promotions, honestly labelled", p: "Featured placements are always clearly marked. Promotion is separate from licence checks and review scores — we never sell approval or alter organic reviews." },
    ],
    cta: { label: "Register a business", to: "/business/register" },
  },
  about: {
    title: "About Real&State",
    eyebrow: "Company",
    intro: "Real&State (a working name) is Dubai's property network — one place for owners, buyers, tenants, investors and property businesses.",
    sections: [
      { icon: Building2, h: "Our mission", p: "Make it simple and trustworthy to find the right property specialist, discuss with your community, and discover projects across Dubai." },
      { icon: ShieldCheck, h: "Honest by design", p: "We do not invent member counts, ratings or verification badges. We are not a licensed broker, and a trade-licence check does not imply government endorsement." },
    ],
  },
  contact: {
    title: "Contact us",
    eyebrow: "Support",
    intro: "This is a concept preview. Contact details below are illustrative.",
    sections: [
      { icon: FileText, h: "General enquiries", p: "hello@example.com (demo address — email delivery is not configured in this preview)." },
      { icon: Building2, h: "Business listings", p: "List your company directly from the For Businesses page." },
    ],
  },
  terms: {
    title: "Terms of Service (draft)",
    eyebrow: "Legal",
    intro: "Draft for owner review — not legal advice. These terms govern use of the Real&State platform.",
    sections: [
      { icon: FileText, h: "Platform role", p: "Real&State is a listing and communication platform. It is not a licensed broker and does not collect service payments or hold escrow in this version." },
      { icon: ShieldCheck, h: "Licence checks", p: "A 'Licence checked' badge confirms a document review only. It does not guarantee service quality, insurance or government approval." },
    ],
  },
  privacy: {
    title: "Privacy Policy (draft)",
    eyebrow: "Legal",
    intro: "Draft for owner review — not legal advice.",
    sections: [
      { icon: ShieldCheck, h: "Your data", p: "Contact details and exact addresses are kept private until you choose to share them with a specific company." },
      { icon: FileText, h: "Private documents", p: "Trade licence documents are stored privately and are never shown to customers or indexed by search engines." },
    ],
  },
  "community-guidelines": {
    title: "Community guidelines",
    eyebrow: "Community",
    intro: "Keep Real&State helpful and respectful.",
    sections: [
      { icon: FileText, h: "Be helpful and honest", p: "Share genuine experiences and advice. No spam, scams or promotional flooding." },
      { icon: Building2, h: "Companies must identify themselves", p: "Company comments show the company relationship and follow moderation rules." },
      { icon: ShieldCheck, h: "Report issues", p: "Use the report tools to flag content. Moderators review reports; moderation is separate from paid visibility." },
    ],
  },
};

export default function StaticPage({ slug }) {
  const c = CONTENT[slug];
  if (!c) return null;
  return (
    <div className="propora-container py-14 max-w-3xl" data-testid={`static-${slug}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#007F83] mb-2">{c.eyebrow}</p>
      <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">{c.title}</h1>
      <p className="text-lg text-[#4A5568] mt-3">{c.intro}</p>
      <div className="mt-10 space-y-6">
        {c.sections.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-[#E3E8E8] p-6 flex gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#E8F5F1] flex items-center justify-center shrink-0"><s.icon size={20} className="text-[#007F83]" /></div>
            <div><h3 className="font-display font-bold text-lg text-[#0B1733]">{s.h}</h3><p className="text-[#4A5568] mt-1">{s.p}</p></div>
          </div>
        ))}
      </div>
      {c.cta && <Link to={c.cta.to}><Button className="mt-8 rounded-full bg-[#007F83] hover:bg-[#006B70] text-white h-11 px-7">{c.cta.label}</Button></Link>}
    </div>
  );
}
